import { isNullOrUndefined } from 'utils/helpers'
import upperFirst from 'lodash/upperFirst'
import { renderToString } from 'react-dom/server'
import { Marker } from 'react-leaflet'
import leaflet from 'leaflet'
import { MarkerPopup } from './components/marker-popup'
import { BrandChip } from '../../brand-monograms'
import { ReactComponent as StarIcon } from 'images/competitor-pricing/star.svg'
import classNames from 'classnames/bind'
import styles from './MapMarkers.module.scss'

const cx = classNames.bind(styles)

const STATUS_COLORS = {
  cheaper: '#ec6278',
  match: '#7c44d2',
  expensive: '#f5ce0a',
  'no-price-data': '#b3bfc9',
  default: '#23303c',
}

const filterPrices = (station, selectedFuelGrade) => {
  const stationSelectedPrices = []
  station.polePrices
    .filter(price => price.fuelGradeId === selectedFuelGrade)
    .map(price =>
      stationSelectedPrices.push({
        pricingType: price?.pricingType,
        price: price?.price,
      })
    )

  const allPrice = stationSelectedPrices.find(
    price => price.pricingType === 'ALL'
  )?.price
  const cashPrice = stationSelectedPrices.find(
    price => price.pricingType === 'CASH'
  )?.price
  const creditPrice = stationSelectedPrices.find(
    price => price.pricingType === 'CREDIT'
  )?.price

  return { allPrice, cashPrice, creditPrice }
}

const checkMarkerClass = (
  myStationSelectedPrice,
  competitorSelectedPrice,
  cashCreditPricingEnabled
) => cashCreditPricingEnabled
    ? 'default'
    : checkPriceClass(myStationSelectedPrice, competitorSelectedPrice)

const checkPriceClass = (myStationSelectedPrice, competitorSelectedPrice) => {
  if (!myStationSelectedPrice) {
    return
  }

  if (!competitorSelectedPrice) {
    return 'no-price-data'
  }
  if (myStationSelectedPrice === competitorSelectedPrice) {
    return 'match'
  }

  if (myStationSelectedPrice > competitorSelectedPrice) {
    return 'cheaper'
  }

  if (myStationSelectedPrice < competitorSelectedPrice) {
    return 'expensive'
  }
}

const parseDrivingDistance = (t, distances) => {
  const distance =
    distances[`drivingDistance${upperFirst(t('distance.title'))}`]
  if (isNullOrUndefined(distance)) {
    return null
  }
  return `${parseFloat(distance).toFixed(1)} ${t('distance.short')} ${t(
    'widgets.competitorPricing.away'
  )}`
}

const createMarker = (
  station,
  validFuelGrades,
  selectedFuelGrade,
  selectedPrice,
  prices,
  markerClass,
  priceClasses,
  isMyStation,
  isCashCreditMarker,
  t,
  stationId,
  stationPolePrices,
  showNearbyStationCompetitors
) => {
  const displayPrice = selectedPrice || prices?.allPrice || null
  const statusClass = isCashCreditMarker
    ? (priceClasses?.allPriceClass || 'default')
    : (markerClass || 'default')
  const statusColor = isMyStation
    ? STATUS_COLORS.default
    : (STATUS_COLORS[statusClass] || STATUS_COLORS.default)

  let iconHtml, iconSize, iconAnchor

  if (isMyStation) {
    const showDualPrice = isCashCreditMarker && (prices?.cashPrice || prices?.creditPrice)

    iconHtml = renderToString(
      <div className={cx('mine-marker')} data-testid="map-markers">
        <div className={cx('mine-tab')}>
          {t('widgets.competitorPricing.myStation')}
        </div>
        <div className={cx('mine-body')}>
          <BrandChip brand={station.brand} size={24} />
          {showDualPrice ? (
            <div className={cx('mine-dual-price')} data-testid="station-marker-price">
              <span>{prices?.cashPrice || '-'}</span>
              <div className={cx('mine-price-divider')} />
              <span>{prices?.creditPrice || '-'}</span>
            </div>
          ) : (
            <span data-testid="station-marker-price">{displayPrice || '-'}</span>
          )}
        </div>
        <div className={cx('mine-tail')} />
      </div>
    )
    iconSize = showDualPrice ? [140, 68] : [100, 68]
    iconAnchor = showDualPrice ? [70, 68] : [50, 68]
  } else {
    const showDualPrice = isCashCreditMarker && (prices?.cashPrice || prices?.creditPrice)
    const hasPrice = showDualPrice || (displayPrice && displayPrice !== '-')

    iconHtml = renderToString(
      <div className={cx('chip-marker')} style={{ '--c': statusColor }} data-testid="map-markers">
        {hasPrice ? (
          <div className={cx('chip')}>
            <span className={cx('chip-logo')}>
              <BrandChip brand={station.brand} size={24} />
            </span>
            {showDualPrice ? (
              <span className={cx('chip-dual-price')} data-testid="competitor-marker-price">
                <span>{prices?.cashPrice || '-'}</span>
                <span className={cx('chip-price-divider')} />
                <span>{prices?.creditPrice || '-'}</span>
              </span>
            ) : (
              <span className={cx('chip-price')} data-testid="competitor-marker-price">{displayPrice}</span>
            )}
          </div>
        ) : (
          <div className={cx('chip-no-price')} data-testid="competitor-marker-price">
            <BrandChip brand={station.brand} size={31} />
          </div>
        )}
        <div className={cx('chip-tail')} />
        {station.influencer && (
          <span className={cx('fav-badge')}>
            <StarIcon />
          </span>
        )}
      </div>
    )
    iconSize = showDualPrice ? [140, 50] : (hasPrice ? [90, 50] : [40, 50])
    iconAnchor = showDualPrice ? [70, 48] : (hasPrice ? [45, 48] : [20, 48])
  }

  const icon = new leaflet.divIcon({
    html: iconHtml,
    iconSize,
    iconAnchor,
    popupAnchor: [0, -iconAnchor[1]],
    className: cx('map-marker'),
  })

  return (
    <Marker
      icon={icon}
      key={station.id}
      position={[station.latitude, station.longitude]}
    >
      <MarkerPopup
        station={station}
        validFuelGrades={validFuelGrades}
        selectedFuelGrade={selectedFuelGrade}
        isMyStation={isMyStation}
        stationId={stationId}
        stationPolePrices={stationPolePrices}
        showNearbyStationCompetitors={showNearbyStationCompetitors}
      />
    </Marker>
  )
}

export {
  STATUS_COLORS,
  filterPrices,
  checkMarkerClass,
  checkPriceClass,
  parseDrivingDistance,
  createMarker,
}
